import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useUpsertCsrPillar } from "src/api/client-api/csrStrategy/Pillars/useUpsertCsrPillar";
import type { CsrPillar } from "src/api/client-api/csrStrategy/useGetAllStrategy";
import { useSettingsStrategy } from "src/context/Settings/CsrStrategy/useSettingsStrategy";
import { MODIFY, STRATEGY, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useSnackbar } from "src/utils/useSnackbar";
import { isNil } from "lodash";
import { formToCsrPillarWrite, type CsrPillarForm } from "./EditPillarForm/PillarForm.utils";
import { ListPillars } from "./ListPillars/ListPillars";
import { PillarDialogState, PillarUpsertDialog } from "./ListPillars/PillarUpsertDialog";
import { pillarToForm } from "./ListPillars/UpsertDialog.utils";

export const Pillars = () => {
    const { strategy } = useSettingsStrategy();
    const theme = useTheme();
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const upsertPillar = useUpsertCsrPillar();
    const { canUser } = useFoodpilotContext();
    const [pillarDialog, setPillarDialog] = useState<PillarDialogState | null>(null);

    const existingPillars = strategy?.pillars ?? [];
    const doItemsExist = existingPillars.length > 0;
    const canManageStrategy = strategy !== undefined && canUser(MODIFY, STRATEGY);
    const addPillar = () => {
        const positions = existingPillars
            .map(({ position }) => position)
            .filter((position): position is number => !isNil(position));
        const position = positions.length > 0 ? Math.max(...positions) + 1 : existingPillars.length;
        setPillarDialog({ mode: "create", pillar: { ...pillarToForm(), position } });
    };
    const editPillar = (pillar: CsrPillar) => setPillarDialog({ mode: "edit", pillar: pillarToForm(pillar) });

    const savePillar = (form: CsrPillarForm) => {
        if (!strategy || !pillarDialog) return;

        const wasCreated = pillarDialog.mode === "create";
        upsertPillar.mutate(
            { payload: formToCsrPillarWrite(form, strategy["@id"]) },
            {
                onSuccess: () => {
                    setPillarDialog(null);
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "info",
                        message:
                            wasCreated ?
                                t(
                                    "settings.csr.pillar_dialog.created_success",
                                    "Your pillar has been successfully added to your strategy!",
                                )
                            :   t(
                                    "settings.csr.pillar_dialog.updated_success",
                                    "Your pillar has been successfully updated!",
                                ),
                    });
                },
                onError: () => {
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({ type: "error", message: t("settings.error", "Error while saving") });
                },
            },
        );
    };

    const reorderPillars = async (pillar: CsrPillar) => {
        if (!strategy || upsertPillar.isLoading) return;

        try {
            await upsertPillar.mutateAsync({
                payload: formToCsrPillarWrite(pillarToForm(pillar), strategy["@id"]),
            });
        } catch (error) {
            snackbar.forceClose();
            snackbar.setSnackbarProps({ type: "error", message: t("settings.error", "Error while saving") });
            throw error;
        }
    };

    return (
        <Stack gap={theme.spacing(2.5)}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={theme.spacing(2)}>
                <Stack direction="row" alignItems="center" gap={theme.spacing(1.5)}>
                    <Typography variant="h2">
                        {t("settings.csr.overview.pillars_title", "Strategic pillars")}
                    </Typography>
                    <Box
                        component="span"
                        sx={{
                            minWidth: theme.spacing(3.5),
                            height: theme.spacing(3.5),
                            px: theme.spacing(1),
                            border: `1px solid ${theme.custom.grey[500]}`,
                            borderRadius: theme.spacing(0.75),
                            backgroundColor: theme.palette.common.white,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="body-medium">{existingPillars.length}</Typography>
                    </Box>
                </Stack>
                {canManageStrategy && (
                    <Button variant="secondary" startIcon={<FoodsIcon icon="plus" size={2} />} onClick={addPillar}>
                        {t("settings.csr.overview.add_pillar", "Add a pillar")}
                    </Button>
                )}
            </Stack>

            {doItemsExist ?
                <Box>
                    <ListPillars
                        pillars={existingPillars}
                        hideHeader
                        onEditPillar={editPillar}
                        onReorderPillars={canManageStrategy ? reorderPillars : undefined}
                        isReordering={upsertPillar.isLoading}
                    />
                </Box>
            :   <Stack
                    alignItems="center"
                    justifyContent="center"
                    gap={theme.spacing(0.75)}
                    minHeight={theme.spacing(19)}
                    sx={{
                        px: theme.spacing(3),
                        py: theme.spacing(4),
                        textAlign: "center",
                        border: `1px dashed ${theme.palette.primary[600]}`,
                        borderRadius: theme.spacing(1.5),
                        backgroundColor: theme.palette.primary[50],
                    }}
                >
                    <Typography variant="big-bold">
                        {t("settings.csr.overview.no_pillar_title", "No pillar yet.")}
                    </Typography>
                    <Typography variant="body" color={theme.custom.grey[2100]}>
                        {t("settings.csr.overview.no_pillar_description", "Add the first pillar to your strategy.")}
                    </Typography>
                    {canManageStrategy && (
                        <Button variant="text" startIcon={<FoodsIcon icon="plus" size={2} />} onClick={addPillar}>
                            {t("settings.csr.overview.add_first_pillar", "Add a first pillar")}
                        </Button>
                    )}
                </Stack>
            }
            {pillarDialog && (
                <PillarUpsertDialog
                    open
                    state={pillarDialog}
                    isSaving={upsertPillar.isLoading}
                    onClose={() => setPillarDialog(null)}
                    onSave={savePillar}
                />
            )}
        </Stack>
    );
};
